"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { MultiSelectFilter, type MultiSelectOption } from "@/components/ui/multi-select-filter"
import {
  Building2,
  ArrowLeft,
  Users,
  Phone,
  Search,
  RefreshCw,
  Plus,
  Bot,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  HardDrive,
  Loader2,
  AlertCircle,
  CreditCard,
  Pencil,
  LogIn,
  ClipboardList,
} from "lucide-react"
import Image from "next/image"
import { TenantEditDialog } from "@/components/tenant-management/tenant-edit-dialog"
import { TenantOnboardingDialog } from "@/components/tenant-management/tenant-onboarding-dialog"
import { TenantReactivationNoticeDialog } from "@/components/tenant-management/tenant-reactivation-notice-dialog"
import { LifecycleActions } from "@/components/tenant-management/lifecycle-actions"
import { LifecycleJobsAlert } from "@/components/tenant-management/lifecycle-jobs-alert"
import { CreateTenantForm } from "@/components/admin/create-tenant-form"
import { useAdminSignupOnboardingSummariesQuery } from "@/hooks/queries/admin-signup-onboarding"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { useImpersonation } from "@/hooks/use-impersonation"
import type { AIBillingMode } from "@/lib/ai/langchain/types"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { filterTenantsBySignupOnboardingStatus } from "@/lib/onboarding/signup-onboarding-filter"
import { SIGNUP_ONBOARDING_STEP_LABELS } from "@/lib/onboarding/signup-onboarding"
import type { TenantWithStats } from "@/app/api/tenants/route"

interface BillingPlan {
  id: string
  name: string
  display_name: string
  billing_channel: string
  status: string
}

type SortField = "name" | "created_at" | "users_count" | "ai_agents_count" | "calls_count" | "storage_bytes"
type SortDirection = "asc" | "desc"

type TenantStatus = "active" | "inactive" | "archived"

const STATUS_OPTIONS: MultiSelectOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
]

const ONBOARDING_STATUS_OPTIONS: MultiSelectOption[] = [
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "skipped", label: "Skipped" },
]

// Sentinels for the faceted filters — tenants without a subscription / channel.
const NO_PLAN_VALUE = "__no_plan__"
const NO_CHANNEL_VALUE = "__no_channel__"

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

/** Humanize a byte count for the Storage column (binary units, 1 decimal). */
function formatStorageBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ["KB", "MB", "GB", "TB"]
  let value = bytes
  let unit = "B"
  for (const next of units) {
    if (value < 1024) break
    value /= 1024
    unit = next
  }
  return `${value.toFixed(1)} ${unit}`
}

const getTenantStatus = (t: TenantWithStats): TenantStatus =>
  t.archived_at ? "archived" : t.is_active ? "active" : "inactive"

export function TenantManagementPage() {
  const { fetchWithAuth, user } = useAuth()
  const { can, canSwitchTenant } = usePermissions()
  const { switching, impersonate } = useImpersonation()
  const activeTenantId = user?.impersonated_tenant_id || user?.tenant_id || null
  // Platform staff are the canonical cross-tenant operators. Delegated users reach
  // this page via `tenants.view` (read) and only see actions they're granted:
  //   - Create → `tenants.create`
  //   - Switch (impersonate) → cross-tenant role (super_user / integrator)
  //   - Onboarding details / free-plan expiry → platform staff
  //   - General edit / lifecycle / Assign Plan → super_user only.
  const isSuperUser = user?.isSuperUser === true
  const canManageOnboardingAndExpiry =
    isSuperUser || user?.isIntegrator === true
  const onboardingSummaries =
    useAdminSignupOnboardingSummariesQuery(canManageOnboardingAndExpiry)
  const canCreateTenant = can(TENANTS_PERMISSIONS.CREATE)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [tenants, setTenants] = useState<TenantWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["active"])
  const [selectedOnboardingStatuses, setSelectedOnboardingStatuses] =
    useState<string[]>([])
  const [selectedPlans, setSelectedPlans] = useState<string[]>([])
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [sortField, setSortField] = useState<SortField>("created_at")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
  const [editTenant, setEditTenant] = useState<TenantWithStats | null>(null)
  const [reactivatedTenantName, setReactivatedTenantName] = useState<string | null>(
    null,
  )
  const [onboardingTenant, setOnboardingTenant] = useState<{
    id: string
    name: string
  } | null>(null)

  // Assign Plan dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignTenant, setAssignTenant] = useState<TenantWithStats | null>(null)
  const [assignPlanId, setAssignPlanId] = useState("")
  const [assignChannel, setAssignChannel] = useState("")
  const [assignLoading, setAssignLoading] = useState(false)
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansError, setPlansError] = useState<string | null>(null)

  const fetchTenants = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetchWithAuth("/api/tenants")
      if (!response.ok) {
        throw new Error("Failed to fetch tenants")
      }
      const data = await response.json()
      setTenants(data.tenants || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error")
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  const loadPlans = async () => {
    if (plans.length > 0) return
    setPlansLoading(true)
    setPlansError(null)
    try {
      const response = await fetchWithAuth("/api/billing/plans")
      if (!response.ok) {
        throw new Error("Failed to load plans")
      }
      const data = await response.json()
      setPlans(data.plans ?? [])
    } catch (err) {
      setPlansError(err instanceof Error ? err.message : "Failed to load plans")
    } finally {
      setPlansLoading(false)
    }
  }

  const openAssignDialog = (tenant: TenantWithStats) => {
    setAssignTenant(tenant)
    setAssignPlanId("")
    // For tenants with existing subscription: lock to current channel.
    // For legacy tenants without subscription: start empty, force explicit selection.
    setAssignChannel(tenant.subscription_status ? (tenant.billing_channel ?? "") : "")
    setAssignDialogOpen(true)
    loadPlans()
  }

  const handleAssignPlan = async () => {
    if (!assignTenant || !assignPlanId || !assignChannel) return
    setAssignLoading(true)
    try {
      const isFirstAssign = !assignTenant.subscription_status
      const payload: Record<string, string> = { plan_id: assignPlanId }
      if (isFirstAssign) payload.billing_channel = assignChannel
      const response = await fetchWithAuth(`/api/admin/billing/tenants/${assignTenant.id}/subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to assign plan")
      }
      setAssignDialogOpen(false)
      setAssignTenant(null)
      fetchTenants()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign plan")
    } finally {
      setAssignLoading(false)
    }
  }

  // Reveal archived after an archive, else the row leaves the active-only
  // view and its purge button is stranded. Empty filter = all, leave as-is.
  const handleLifecycleChanged = useCallback(
    (completed?: "archive" | "restore" | "purge") => {
      if (completed === "archive") {
        setSelectedStatuses((prev) =>
          prev.length === 0 || prev.includes("archived") ? prev : [...prev, "archived"],
        )
      }
      fetchTenants()
    },
    [fetchTenants],
  )

  useEffect(() => {
    fetchTenants()
  }, [fetchTenants])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-muted-foreground/50" />
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="w-4 h-4 ml-1" />
    ) : (
      <ArrowDown className="w-4 h-4 ml-1" />
    )
  }

  // Plan filter options derived from the tenants currently loaded (faceted),
  // plus a "No plan" bucket for tenants without a subscription. The value is
  // plan_name (unique per plan); the label is the display name. Channel-mirror
  // plans (e.g. free-stripe / free-shopify) share a display name, so when two
  // distinct plan_names collide on the label we suffix the channel to keep the
  // checkboxes distinguishable.
  const planOptions = useMemo<MultiSelectOption[]>(() => {
    const byName = new Map<string, { label: string; channel: string | null }>()
    let hasNoPlan = false
    for (const t of tenants) {
      if (t.plan_name) {
        if (!byName.has(t.plan_name)) {
          byName.set(t.plan_name, {
            label: t.plan_display_name ?? t.plan_name,
            channel: t.billing_channel,
          })
        }
      } else {
        hasNoPlan = true
      }
    }
    const labelCounts = new Map<string, number>()
    for (const { label } of byName.values()) {
      labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1)
    }
    const options = Array.from(byName, ([value, { label, channel }]) => {
      if ((labelCounts.get(label) ?? 0) <= 1) return { value, label }
      const suffix = channel ? capitalize(channel) : value
      return { value, label: `${label} (${suffix})` }
    }).sort((a, b) => a.label.localeCompare(b.label))
    if (hasNoPlan) options.push({ value: NO_PLAN_VALUE, label: "No plan" })
    return options
  }, [tenants])

  // Billing-channel filter options, faceted from the loaded tenants plus a
  // "No channel" bucket for tenants without one (legacy / no subscription).
  const channelOptions = useMemo<MultiSelectOption[]>(() => {
    const channels = new Set<string>()
    let hasNoChannel = false
    for (const t of tenants) {
      if (t.billing_channel) channels.add(t.billing_channel)
      else hasNoChannel = true
    }
    const options = Array.from(channels)
      .sort()
      .map((value) => ({ value, label: capitalize(value) }))
    if (hasNoChannel) options.push({ value: NO_CHANNEL_VALUE, label: "No channel" })
    return options
  }, [tenants])

  const onboardingByTenant = useMemo(
    () =>
      new Map(
        (onboardingSummaries.data ?? []).map((summary) => [
          summary.tenant_id,
          summary,
        ]),
      ),
    [onboardingSummaries.data],
  )

  const filteredAndSortedTenants = useMemo(() => {
    let result = [...tenants]

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter((tenant) => tenant.name.toLowerCase().includes(query))
    }

    // Filter by status (empty selection = all statuses)
    if (selectedStatuses.length > 0) {
      result = result.filter((tenant) => selectedStatuses.includes(getTenantStatus(tenant)))
    }

    result = filterTenantsBySignupOnboardingStatus(
      result,
      onboardingByTenant,
      selectedOnboardingStatuses,
    )

    // Filter by plan (empty selection = all plans)
    if (selectedPlans.length > 0) {
      result = result.filter((tenant) =>
        selectedPlans.includes(tenant.plan_name ?? NO_PLAN_VALUE),
      )
    }

    // Filter by billing channel (empty selection = all channels)
    if (selectedChannels.length > 0) {
      result = result.filter((tenant) =>
        selectedChannels.includes(tenant.billing_channel ?? NO_CHANNEL_VALUE),
      )
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name)
          break
        case "created_at":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case "users_count":
          comparison = a.users_count - b.users_count
          break
        case "ai_agents_count":
          comparison = a.ai_agents_count - b.ai_agents_count
          break
        case "calls_count":
          comparison = a.calls_count - b.calls_count
          break
        case "storage_bytes":
          // null (usage RPC failed) sorts as the lowest so it doesn't pose as a big number.
          comparison = (a.storage_bytes ?? -1) - (b.storage_bytes ?? -1)
          break
      }
      return sortDirection === "asc" ? comparison : -comparison
    })

    return result
  }, [
    tenants,
    searchQuery,
    selectedStatuses,
    selectedOnboardingStatuses,
    selectedPlans,
    selectedChannels,
    onboardingByTenant,
    sortField,
    sortDirection,
  ])

  // Calculate totals
  const totals = useMemo(() => {
    return {
      tenants: tenants.length,
      activeTenants: tenants.filter((t) => getTenantStatus(t) === "active").length,
      users: tenants.reduce((sum, t) => sum + t.users_count, 0),
      aiAgents: tenants.reduce((sum, t) => sum + t.ai_agents_count, 0),
      voiceAgents: tenants.reduce((sum, t) => sum + t.voice_agents_count, 0),
      emailAgents: tenants.reduce((sum, t) => sum + t.email_agents_count, 0),
      calls: tenants.reduce((sum, t) => sum + t.calls_count, 0),
    }
  }, [tenants])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const daysUntil = (dateString: string) => {
    const ms = new Date(dateString).getTime() - Date.now()
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24))
    if (days <= 0) return "any moment now"
    if (days === 1) return "in 1 day"
    return `in ${days} days`
  }

  const handleTenantUpdated = (updated: {
    id: string
    name?: string
    logo_url?: string | null
    disable_free_plan_expiry?: boolean
    is_active?: boolean
    conversation_quality_platform_enabled?: boolean
    conversation_quality_billing_mode?: AIBillingMode
  }) => {
    setTenants((prev) =>
      prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)),
    )
    setEditTenant((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev))
  }

  const handleTenantReactivated = (tenant: { id: string; name: string }) => {
    setEditTenant(null)
    setReactivatedTenantName(tenant.name)
  }

  if (showCreateForm) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => setShowCreateForm(false)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>Back to Tenant Management</span>
          </Button>
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Create New Tenant</h1>
            <p className="text-muted-foreground">Create a new organization with an admin user</p>
          </div>
          <CreateTenantForm
            onSuccess={() => {
              setShowCreateForm(false)
              fetchTenants()
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tenant Management</h1>
          <p className="text-muted-foreground mt-1">Manage organizations and view statistics</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              void fetchTenants()
              if (canManageOnboardingAndExpiry) void onboardingSummaries.refetch()
            }}
            disabled={loading || onboardingSummaries.isFetching}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </Button>
          {canCreateTenant && (
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              <span>Create Tenant</span>
            </Button>
          )}
        </div>
      </div>

      {/* Failed lifecycle jobs (unattended cron / manual) — super-user observability */}
      <LifecycleJobsAlert />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Tenants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold">{totals.tenants}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.activeTenants} <span>active</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold">{totals.users}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              <span>avg</span> {totals.tenants ? (totals.users / totals.tenants).toFixed(1) : 0}<span>/tenant</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">AI Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-500" />
              <span className="text-2xl font-bold">{totals.aiAgents}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.voiceAgents} <span>voice,</span> {totals.emailAgents} <span>email</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Calls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-orange-500" />
              <span className="text-2xl font-bold">{totals.calls}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">all time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Newest Tenant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-500" />
              <span className="text-sm font-medium truncate">
                {tenants[0]?.name || "-"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {tenants[0] ? formatDate(tenants[0].created_at) : "-"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <CardTitle>All Tenants</CardTitle>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
              <div className="flex flex-wrap gap-2">
                <MultiSelectFilter
                  labelPrefix="Status"
                  pluralNoun="statuses"
                  availableOptions={STATUS_OPTIONS}
                  selectedValues={selectedStatuses}
                  onChange={setSelectedStatuses}
                  popoverWidthClassName="w-[200px]"
                />
                {canManageOnboardingAndExpiry ? (
                  <MultiSelectFilter
                    labelPrefix="Onboarding"
                    pluralNoun="statuses"
                    availableOptions={ONBOARDING_STATUS_OPTIONS}
                    selectedValues={selectedOnboardingStatuses}
                    onChange={setSelectedOnboardingStatuses}
                    popoverWidthClassName="w-[200px]"
                  />
                ) : null}
                <MultiSelectFilter
                  labelPrefix="Plan"
                  pluralNoun="plans"
                  availableOptions={planOptions}
                  selectedValues={selectedPlans}
                  onChange={setSelectedPlans}
                />
                <MultiSelectFilter
                  labelPrefix="Channel"
                  pluralNoun="channels"
                  availableOptions={channelOptions}
                  selectedValues={selectedChannels}
                  onChange={setSelectedChannels}
                  popoverWidthClassName="w-[200px]"
                />
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-destructive gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          ) : filteredAndSortedTenants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Building2 className="w-12 h-12 mb-4" />
              <p>{tenants.length === 0 ? "No tenants yet" : "No tenants match your filters"}</p>
              {tenants.length === 0 && canCreateTenant && (
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span>Create your first tenant</span>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      <span>Tenant</span>
                      <SortIcon field="name" />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>
                    <div className="flex items-center">
                      <CreditCard className="w-4 h-4 mr-1" />
                      <span>Plan</span>
                    </div>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("users_count")}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      <span>Users</span>
                      <SortIcon field="users_count" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("ai_agents_count")}
                    >
                      <Bot className="w-4 h-4 mr-1" />
                      <span>Agents</span>
                      <SortIcon field="ai_agents_count" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("calls_count")}
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      <span>Calls</span>
                      <SortIcon field="calls_count" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("storage_bytes")}
                      title="Total storage across all buckets: message media, call recordings (PBX + voice agents), and knowledge-base files"
                    >
                      <HardDrive className="w-4 h-4 mr-1" />
                      <span>Storage</span>
                      <SortIcon field="storage_bytes" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      className="flex items-center hover:text-foreground transition-colors"
                      onClick={() => handleSort("created_at")}
                    >
                      <span>Created</span>
                      <SortIcon field="created_at" />
                    </button>
                  </TableHead>
                  <TableHead>Legal</TableHead>
                  {canSwitchTenant && <TableHead>Switch</TableHead>}
                  {/* Pinned right so the action icons stay visible when the table scrolls horizontally. */}
                  {canManageOnboardingAndExpiry && (
                    <TableHead className="w-28 sticky right-0 bg-card" />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedTenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {tenant.logo_url ? (
                            <Image
                              src={tenant.logo_url}
                              alt={tenant.name}
                              width={32}
                              height={32}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <Building2 className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {tenant.id.slice(0, 8)}<span>...</span>
                          </div>
                          {canManageOnboardingAndExpiry &&
                          !onboardingSummaries.isLoading &&
                          !onboardingSummaries.error &&
                          (onboardingByTenant.get(tenant.id)?.status ??
                            "completed") !== "completed" ? (
                            <button
                              type="button"
                              className="mt-1.5 flex items-center gap-1.5 text-left"
                              onClick={() =>
                                setOnboardingTenant({
                                  id: tenant.id,
                                  name: tenant.name,
                                })
                              }
                              title="View signup onboarding details"
                            >
                              <Badge
                                variant="outline"
                                className={
                                  onboardingByTenant.get(tenant.id)?.status ===
                                  "skipped"
                                    ? "border-amber-300 bg-amber-50 text-amber-800"
                                    : "border-blue-300 bg-blue-50 text-blue-800"
                                }
                              >
                                <span>Onboarding</span>{" "}
                                <span>{onboardingByTenant.get(tenant.id)?.status ===
                                "skipped"
                                  ? "skipped"
                                  : "in progress"}</span>
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {
                                  SIGNUP_ONBOARDING_STEP_LABELS[
                                    onboardingByTenant.get(tenant.id)
                                      ?.current_step ?? "review"
                                  ]
                                }
                              </span>
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {tenant.archived_at ? (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="destructive" className="cursor-default">
                                Archived
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {tenant.archive_scheduled_purge_at
                                ? `Purges on ${formatDate(tenant.archive_scheduled_purge_at)} (${daysUntil(tenant.archive_scheduled_purge_at)})`
                                : "Pending purge"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge variant={tenant.is_active ? "default" : "secondary"}>
                          {tenant.is_active ? "Active" : "Inactive"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.plan_display_name ? (
                        <Badge variant="outline">
                          {tenant.plan_display_name}
                          <span>{tenant.billing_channel ? ` (${capitalize(tenant.billing_channel)})` : ""}</span>
                        </Badge>
                      ) : isSuperUser ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground h-7 px-2"
                          onClick={() => openAssignDialog(tenant)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          <span>Assign Plan</span>
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{tenant.users_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{tenant.ai_agents_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{tenant.calls_count}</span>
                    </TableCell>
                    <TableCell>
                      {tenant.storage_bytes === null ? (
                        <span className="text-muted-foreground" title="Storage usage could not be computed (RPC failed)">n/a</span>
                      ) : tenant.storage_bytes > 0 ? (
                        <span className="font-medium">{formatStorageBytes(tenant.storage_bytes)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(tenant.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {tenant.terms_accepted_at ? (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="cursor-default border-green-300 bg-green-50 text-green-800"
                              >
                                Accepted
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="space-y-1">
                                <p>{formatDate(tenant.terms_accepted_at)}</p>
                                <p className="text-xs">
                                  <span>Terms v</span>{tenant.terms_version ?? "?"} <span>· Privacy v</span>
                                  {tenant.privacy_policy_version ?? "?"}
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge variant="secondary" className="cursor-default">
                          Not recorded
                        </Badge>
                      )}
                    </TableCell>
                    {canSwitchTenant && (
                      <TableCell>
                        {tenant.id === activeTenantId ? (
                          <Badge variant="secondary" className="cursor-default">
                            Current
                          </Badge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => impersonate(tenant.id)}
                            disabled={switching || !!tenant.archived_at}
                            title={
                              tenant.archived_at
                                ? "Archived tenants can't be impersonated"
                                : `Switch to ${tenant.name}`
                            }
                          >
                            <LogIn className="w-3.5 h-3.5 mr-1" />
                            <span>Switch</span>
                          </Button>
                        )}
                      </TableCell>
                    )}
                    {canManageOnboardingAndExpiry && (
                      <TableCell className="sticky right-0 bg-card">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              setOnboardingTenant({
                                id: tenant.id,
                                name: tenant.name,
                              })
                            }
                            title="View signup onboarding"
                          >
                            <ClipboardList className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditTenant(tenant)}
                            title={
                              isSuperUser
                                ? "Edit tenant"
                                : "Edit free plan expiry"
                            }
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isSuperUser && (
                            <LifecycleActions
                              tenant={{
                                id: tenant.id,
                                name: tenant.name,
                                archived_at: tenant.archived_at,
                                archive_scheduled_purge_at:
                                  tenant.archive_scheduled_purge_at,
                              }}
                              isOwnTenant={tenant.id === activeTenantId}
                              onChanged={handleLifecycleChanged}
                            />
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TenantEditDialog
        tenant={editTenant}
        onOpenChange={(open) => !open && setEditTenant(null)}
        canEditTenantDetails={isSuperUser}
        canChangeTenantStatus={canManageOnboardingAndExpiry}
        isPlatformTenant={editTenant?.id === user?.tenant_id}
        onReactivated={handleTenantReactivated}
        onUpdated={handleTenantUpdated}
      />

      <TenantReactivationNoticeDialog
        tenantName={reactivatedTenantName}
        onOpenChange={(open) => !open && setReactivatedTenantName(null)}
      />

      <TenantOnboardingDialog
        tenant={onboardingTenant}
        onOpenChange={(open) => !open && setOnboardingTenant(null)}
      />

      {/* Assign Plan Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{assignTenant?.subscription_status ? 'Change Plan' : 'Initialize Billing'}</DialogTitle>
            <DialogDescription>
              {assignTenant?.subscription_status
                ? <>Change billing plan for <strong>{assignTenant?.name}</strong>.</>
                : <>Initialize billing for <strong>{assignTenant?.name}</strong>. Choose billing channel and first plan. The tenant will start on platform-managed (manual) billing.</>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Channel selector — only for first assignment */}
            {!assignTenant?.subscription_status && (
              <div>
                <p className="text-sm font-medium mb-1.5">Billing Channel</p>
                <Select value={assignChannel} onValueChange={(v) => { setAssignChannel(v); setAssignPlanId("") }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="shopify">Shopify</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Cannot be changed after first plan assignment.</p>
              </div>
            )}

            {/* Plan selector */}
            {plansLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading plans...</span>
              </div>
            ) : plansError ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-destructive">{plansError}</p>
                <Button variant="outline" size="sm" onClick={loadPlans}>
                  Retry
                </Button>
              </div>
            ) : !assignChannel ? (
              <p className="text-sm text-muted-foreground">Select a billing channel first.</p>
            ) : (() => {
              const channelPlans = plans.filter((p) => p.billing_channel === assignChannel && p.status !== 'draft')
              return channelPlans.length === 0 ? (
                <p className="text-sm text-muted-foreground"><span>No plans available for</span> {assignChannel} <span>channel.</span></p>
              ) : (
                <Select value={assignPlanId} onValueChange={setAssignPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {channelPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.display_name} <span className="text-muted-foreground">({plan.name})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} disabled={assignLoading}>
              Cancel
            </Button>
            <Button onClick={handleAssignPlan} disabled={assignLoading || !assignPlanId}>
              {assignLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <span>Assign Plan</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
